import { useQueryClient } from '@tanstack/react-query';
import {
  getV1MessagingChannels,
  MessagingChannel_Create,
  MessagingChannelReplyMode,
  MessagingChannelType,
  ModelError_Item,
  ModelError_Response,
  postV1MessagingChannels,
  postV1MessagingChannelsMessagingChannelIdMessages,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import {
  isMessagingChannelTargetReference,
  MessagingChannel,
} from '~hooks/useMessaging/models';
import { MESSAGING_QUERY_KEY } from '~queries/messagingQueryKeys';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';
import { Nullable } from '~types/Nullable';

type StartConversationInput = {
  channelType: MessagingChannelType;
  initialMessage?: string;
  jobId?: string;
  name?: string;
  orderId?: string;
  recipientUserIds?: string[];
  replyMode?: MessagingChannelReplyMode;
  scopedCompanyId?: string;
};

/**
 * Create is open-or-create for a job/order, so a field-keyed 422 means the target cannot hold a
 * conversation (a blank id, or a job with no driver); a conflict is only ever proven by finding
 * the conversation, which is why this carries it.
 */
export class MessagingTargetConflictError extends Error {
  constructor(public readonly existingChannel: MessagingChannel) {
    super('The target already has a live conversation');
    this.name = 'MessagingTargetConflictError';
  }
}

export class MessagingRecipientsUnreachableError extends Error {
  constructor(public readonly userIds: string[]) {
    super('Some broadcast recipients cannot be reached');
    this.name = 'MessagingRecipientsUnreachableError';
  }
}

const trimmedOrUndefined = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const CREATE_TARGET_FIELD: Partial<Record<MessagingChannelType, string>> = {
  [MessagingChannelType.JOB]: 'job_id',
  [MessagingChannelType.ORDER]: 'order_id',
};

const unprocessableModelErrorsOf = (error: unknown): ModelError_Item[] =>
  isAxiosError<ModelError_Response>(error) && error.response?.status === 422
    ? (error.response.data.error.errors ?? [])
    : [];

export const isTargetFieldRejection = (
  error: unknown,
  channelType: MessagingChannelType,
) => {
  const targetField = CREATE_TARGET_FIELD[channelType];
  if (!targetField) return false;

  return unprocessableModelErrorsOf(error).some(
    (modelError) => modelError.field === targetField,
  );
};

const unreachableRecipientIdsOf = (error: unknown) =>
  unprocessableModelErrorsOf(error).flatMap((modelError) =>
    modelError.field === 'recipient_user_ids' && modelError.user_ids
      ? modelError.user_ids
      : [],
  );

// Bounded so a company with thousands of conversations cannot hang the dialog.
const CONFLICT_LOOKUP_MAX_PAGES = 5;

const findExistingTargetChannel = async ({
  channelType,
  targetId,
}: {
  channelType: MessagingChannelType;
  targetId: string;
}): Promise<Nullable<MessagingChannel>> => {
  let cursor: string | undefined;

  for (let pageIndex = 0; pageIndex < CONFLICT_LOOKUP_MAX_PAGES; pageIndex += 1) {
    const response = await getV1MessagingChannels({
      query: {
        'page[limit]': MAX_API_PAGE_SIZE,
        ...(cursor ? { 'page[after]': cursor } : {}),
        'filter[channel_types]': [channelType],
      },
    });

    const match = response.data.data
      .map(MessagingChannel.parse)
      .find(
        (channel) =>
          !channel.isArchived &&
          channel.target &&
          isMessagingChannelTargetReference(channel.target) &&
          channel.target.id === targetId,
      );
    if (match) return match;

    cursor = extractPagination(response).after;
    if (!cursor) return null;
  }

  return null;
};

/** Create takes no message body; a failed opening message still resolves the channel. */
export default function useStartConversation() {
  const queryClient = useQueryClient();

  const startConversation = useCallback(
    async ({
      initialMessage,
      ...channelInput
    }: StartConversationInput): Promise<MessagingChannel> => {
      let channel: MessagingChannel;
      try {
        const response = await postV1MessagingChannels({
          body: buildChannelCreatePayload(channelInput),
        });
        channel = MessagingChannel.parse(response.data.data);
      } catch (error) {
        const requestedRecipientIds = new Set(channelInput.recipientUserIds);
        const unreachableRecipientIds = unreachableRecipientIdsOf(error).filter(
          (userId) => requestedRecipientIds.has(userId),
        );
        if (unreachableRecipientIds.length > 0) {
          throw new MessagingRecipientsUnreachableError(unreachableRecipientIds);
        }

        if (isTargetFieldRejection(error, channelInput.channelType)) {
          // The dialog keeps a job selection after a switch to order, so only the id
          // matching the type can be trusted as the subject.
          const targetId =
            channelInput.channelType === MessagingChannelType.JOB
              ? channelInput.jobId
              : channelInput.orderId;

          let existingChannel: Nullable<MessagingChannel> = null;
          if (targetId) {
            try {
              existingChannel = await findExistingTargetChannel({
                channelType: channelInput.channelType,
                targetId,
              });
            } catch {
              // A failed lookup leaves the 422 to be reported as itself below.
            }
          }

          if (existingChannel) {
            throw new MessagingTargetConflictError(existingChannel);
          }
        }

        connection.handleRequestError(
          error,
          t('error_messages.messaging.failed_to_create_channel'),
        );
        throw error;
      }

      const openingMessage = trimmedOrUndefined(initialMessage);
      if (openingMessage) {
        try {
          await postV1MessagingChannelsMessagingChannelIdMessages({
            path: { 'messaging-channel-id': channel.id },
            body: { content: openingMessage },
          });
        } catch (error) {
          // Not rethrown: the channel exists, and a retried create would duplicate it.
          reportRequestError(error, t('error_messages.messaging.failed_to_send_message'));
        }
      }

      await queryClient.invalidateQueries({ queryKey: [MESSAGING_QUERY_KEY] });
      return channel;
    },
    [queryClient],
  );

  return { startConversation };
}

/** The api ignores recipients on job/order, and rejects a reply mode on direct. */
export const buildChannelCreatePayload = ({
  channelType,
  jobId,
  name,
  orderId,
  recipientUserIds,
  replyMode,
  scopedCompanyId,
}: Omit<StartConversationInput, 'initialMessage'>): MessagingChannel_Create => {
  const base = { channel_type: channelType, name: trimmedOrUndefined(name) };

  switch (channelType) {
    case MessagingChannelType.JOB:
      return { ...base, job_id: jobId, reply_mode: replyMode };
    case MessagingChannelType.ORDER:
      return { ...base, order_id: orderId, reply_mode: replyMode };
    case MessagingChannelType.BROADCAST:
      return {
        ...base,
        recipient_user_ids: recipientUserIds,
        reply_mode: replyMode,
        scoped_company_id: scopedCompanyId,
      };
    case MessagingChannelType.DIRECT:
      return { ...base, recipient_user_ids: recipientUserIds };
    default:
      throw new Error(`Unsupported messaging channel type: ${channelType}`);
  }
};
